import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Dialog from '@radix-ui/react-dialog';
import { useRef, useState } from 'react';
import { Button } from '../../atoms/Button/Button.js';
import { cn } from '../../lib/cn.js';
import { OVERLAY_SURFACE } from '../../lib/surface-styles.js';
/**
 * A dialog action: full width with a label that may wrap on a phone, the
 * button's own single line from `sm` up. `min-h` rather than `h`, so a
 * two-line label makes the button taller instead of spilling out of it.
 */
const DIALOG_ACTION = cn('w-full h-auto min-h-control-md py-2 leading-snug whitespace-normal text-balance', 'sm:w-auto sm:whitespace-nowrap');
/**
 * Confirmation dialog (handoff, Molecules).
 *
 * Radix owns the parts that are easy to get wrong and invisible when you do:
 * the focus trap, Escape, returning focus to the trigger, and marking the rest
 * of the page inert. What is written here is the shape and the contract.
 *
 * ── Where focus starts ───────────────────────────────────────────────────
 *
 * On the first thing in the dialog that takes focus. With nothing but the two
 * buttons, that is the way out, because it comes first — so Enter pressed out
 * of habit never goes ahead. A dialog with fields starts in its first field,
 * which is where the person has to be before anything can go ahead, and
 * typing in a field activates nothing.
 *
 * ── What the buttons look like ───────────────────────────────────────────
 *
 * Decided by `intent` (see `ConfirmationIntent`), never inferred from a label.
 * Colour is never the only signal: the recommended option is the filled one,
 * it comes first, and both labels say what they do.
 */
export function ConfirmationDialog({ open, onOpenChange, intent, title, description, confirmLabel, confirmingLabel, confirmDisabled = false, onConfirm, cancelLabel, onCancel, onCloseAutoFocus, trigger, className, children, failure, }) {
    const previousFocus = useRef(null);
    const [confirming, setConfirming] = useState(false);
    // Held in a ref as well, so a second press in the same tick is refused
    // before the state that disables it has rendered.
    const inFlight = useRef(false);
    function changeOpen(next) {
        // Nothing closes the dialog while its action is on its way: the answer is
        // what decides whether it closes.
        if (!next && inFlight.current)
            return;
        onOpenChange(next);
    }
    async function confirm() {
        if (inFlight.current)
            return;
        const outcome = onConfirm();
        // Decided the moment it was chosen: it has run, so the dialog goes. A
        // caller that also answers the close — a navigation guard that stays
        // unless told to leave — sees "leave" first, and the close second.
        if (!outcome) {
            onOpenChange(false);
            return;
        }
        inFlight.current = true;
        setConfirming(true);
        // A rejected action is an action not done.
        let done;
        try {
            done = await outcome;
        }
        catch {
            done = false;
        }
        finally {
            inFlight.current = false;
            setConfirming(false);
        }
        if (done)
            onOpenChange(false);
    }
    const cancelVariant = intent === 'default' ? 'secondary' : 'primary';
    const confirmVariant = intent === 'default' ? 'primary' : intent === 'caution' ? 'secondary' : 'destructive';
    return (_jsxs(Dialog.Root, { open: open, onOpenChange: changeOpen, children: [trigger ? _jsx(Dialog.Trigger, { asChild: true, children: trigger }) : null, _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "fixed inset-0 z-50 bg-text/40" }), _jsxs(Dialog.Content, { "data-intent": intent, onOpenAutoFocus: () => {
                            previousFocus.current =
                                document.activeElement instanceof HTMLElement ? document.activeElement : null;
                        }, onCloseAutoFocus: (event) => {
                            onCloseAutoFocus?.(event);
                            if (event.defaultPrevented)
                                return;
                            if (!trigger && previousFocus.current?.isConnected) {
                                event.preventDefault();
                                previousFocus.current.focus();
                            }
                        }, className: cn(OVERLAY_SURFACE, 'fixed left-1/2 top-1/2 z-50 w-[min(30rem,calc(100vw-2rem))]', 
                        // Never taller than the screen: a phone with the keyboard up still
                        // reaches both buttons by scrolling the dialog, not the page.
                        'max-h-[calc(100dvh-2rem)] overflow-y-auto', '-translate-x-1/2 -translate-y-1/2 p-6 sm:p-7', 'flex flex-col gap-4', className), children: [_jsx(Dialog.Title, { className: "text-h2 font-bold text-text text-balance wrap-break-word", children: title }), _jsx(Dialog.Description, { className: "text-body text-text-secondary text-pretty wrap-break-word", children: description }), children, failure, _jsxs("div", { className: "mt-2 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end", children: [_jsx(Button, { className: DIALOG_ACTION, variant: cancelVariant, 
                                        // Held while the action runs, so the answer decides what
                                        // happens; focus is on the confirm then, so nothing is lost.
                                        disabled: confirming, onClick: () => {
                                            onCancel?.();
                                            changeOpen(false);
                                        }, children: cancelLabel }), _jsx(Button, { className: DIALOG_ACTION, variant: confirmVariant, disabled: confirmDisabled, loading: confirming, ...(confirmingLabel ? { loadingText: confirmingLabel } : {}), onClick: () => void confirm(), children: confirmLabel })] })] })] })] }));
}
//# sourceMappingURL=ConfirmationDialog.js.map